import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "seller_helper_onboarding_completed";

export type OnboardingStep =
    | "ready"        // 준비 (시작 전)
    | "nickname"     // 1단계: 닉네임 입력
    | "addItem"      // 2단계: 상품 추가
    | "settings"     // 3단계: 배송/결제 설정
    | "orderControls" // 4단계: 주문 상태/보관 설정
    | "checkBill"    // 5단계: 정산서 확인 및 복사 (마지막)
    | "completed";   // 완료

export interface OnboardingState {
    isActive: boolean;
    currentStep: OnboardingStep;
    isCompleted: boolean;
}

interface UseOnboardingProps {
    enabled?: boolean;
    loading?: boolean;
}

export function useOnboarding({ enabled = true, loading = false }: UseOnboardingProps = {}) {
    const [isCompleted, setIsCompleted] = useState<boolean>(true); // 기본값 true로 깜빡임 방지
    const [isActive, setIsActive] = useState(false);
    const [currentStep, setCurrentStep] = useState<OnboardingStep>("ready");

    // 초기 상태 로드
    useEffect(() => {
        if (loading) return; // 로딩 중이면 대기
        if (!enabled) { // 활성화되지 않았으면 (예: 유료 회원) 투어 시작 안함
            setIsCompleted(true);
            return;
        }

        const completed = localStorage.getItem(STORAGE_KEY) === "true";
        setIsCompleted(completed);

        // 완료되지 않았으면 투어 준비
        if (!completed) {
            // 약간의 딜레이 후 시작 (페이지 로딩 안정화)
            const timer = setTimeout(() => {
                setIsActive(true);
                setCurrentStep("nickname");
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [enabled, loading]);

    const startTour = useCallback(() => {
        setIsActive(true);
        setCurrentStep("nickname");
        setIsCompleted(false);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    const nextStep = useCallback(() => {
        setCurrentStep((prev) => {
            switch (prev) {
                case "nickname": return "addItem";
                case "addItem": return "settings";
                case "settings": return "orderControls";
                case "orderControls": return "checkBill";
                case "checkBill": return "completed";
                default: return prev;
            }
        });
    }, []);

    const prevStep = useCallback(() => {
        setCurrentStep((prev) => {
            switch (prev) {
                case "addItem": return "nickname";
                case "settings": return "addItem";
                case "orderControls": return "settings";
                case "checkBill": return "orderControls";
                default: return prev;
            }
        });
    }, []);

    const goToStep = useCallback((step: OnboardingStep) => {
        setCurrentStep(step);
    }, []);

    const completeTour = useCallback(() => {
        setIsActive(false);
        setCurrentStep("completed");
        setIsCompleted(true);
        localStorage.setItem(STORAGE_KEY, "true");
    }, []);

    const skipTour = useCallback(() => {
        setIsActive(false);
        setCurrentStep("ready"); // 완료 상태는 아님 (나중에 다시 볼 수 있게? 기획에 따라 다름. 여기선 일단 완료 처리)
        setIsCompleted(true);
        localStorage.setItem(STORAGE_KEY, "true");
    }, []);

    // 특정 스텝 강제 이동 (예: 사용자가 미리 행동했을 때)
    const syncStep = useCallback((step: OnboardingStep) => {
        if (!isActive) return;
        setCurrentStep(step);
    }, [isActive]);

    return {
        isActive,
        currentStep,
        isCompleted,
        startTour,
        nextStep,
        prevStep, // Added
        goToStep,
        completeTour,
        skipTour,
        syncStep
    };
}
